import type Account from "@travily/types/models/Account";
import type MessagesMessage from "@travily/types/models/MessagesMessage";
import type User from "@travily/types/models/User";
import type { AlertStatus } from "@ui/components/Alert";
import type { SearchMember } from "@ui/components/SearchBar";
import type { ToastStatus } from "@ui/components/Toast";

export type { UserStatus } from "@travily/types/models/Enums";

export interface FlashToastMessage {
  title: string;
  description: string;
  list?: string[];
  status?: ToastStatus;
}

export interface FlashAlertMessage {
  title: string;
  description: string;
  list?: string[];
  status?: AlertStatus;
}

export type FlashToast = string | FlashToastMessage;
export type FlashAlert = string | FlashAlertMessage;

export interface Flash {
  toast?: FlashToast;
  alert?: FlashAlert;
}

export function isFlashMessage<T extends FlashToastMessage | FlashAlertMessage>(
  value: string | T | undefined,
): value is T {
  return typeof value === "object" && value !== null && "title" in value && "description" in value;
}

export function normalizeFlashValue(
  value: FlashToast | FlashAlert | undefined,
): { title: string; description: string } | null {
  if (typeof value === "string") return { title: value, description: "" };
  if (isFlashMessage(value)) return { title: value.title, description: value.description };
  return null;
}

export type { Notification, NotificationAction } from "../hooks/use-notification-channel";

/** The thread behind the message dock, served on any page whose visit carried `X-Docked-Chat`. */
export interface DockedConversation {
  /** Echo of the requested handle, so a response for a since-closed dock can be discarded. */
  handle: string;
  other_account: Account;
  /** Null until a first message opens the thread. */
  conversation: { sqid: string } | null;
  messages: MessagesMessage[];
  counterpart_read_at: string | null;
  reactions: Record<string, Array<{ emoji: string; count: number; reacted: boolean }>>;
  /** Null until a conversation exists — there is nothing to subscribe to yet. */
  cable_stream: string | null;
  /** Signed token for the (not yet built) ConversationTypingChannel. Null with no conversation. */
  typing_stream: string | null;
}

export type SharedProps = {
  flash: Flash;
  current_user: User | null;
  current_account: Account | null;
  accounts: Account[] | null;
  /** The viewer's family members for the SearchBar "Who" section — empty for non-family accounts. */
  family_members: SearchMember[];
  intercom: { user_jwt: string | null } | null;
  unseen_notifications_count: number;
  unread_messages_count: number;
  notifications: import("../hooks/use-notification-channel").Notification[];
  locale: string;
  currency: string;
  suggested_locales: string[];
  exchange_rates: Record<string, number>;
  has_listings: boolean;
  stripe_connected: boolean;
  docked_conversation?: DockedConversation | null;
};
